/**
 * Single inline-SVG icon registry. Every icon inherits `currentColor` so the
 * surrounding tile controls its colour.
 */

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

/** Icons whose artwork reads better at a slightly heavier weight. */
const WIDTHS = { check: 1.7 };

const paths = {
  layers: (
    <>
      <path d="M12 2 2 7l10 5 10-5-10-5Z" />
      <path d="m2 17 10 5 10-5M2 12l10 5 10-5" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </>
  ),
  crosshair: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v18M3 12h18" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v18h18" />
      <path d="m19 9-5 5-4-4-3 3" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    </>
  ),
  phone: <path d="M2 3h6l2 4-3 2a12 12 0 0 0 6 6l2-3 4 2v6a2 2 0 0 1-2 2A18 18 0 0 1 2 5a2 2 0 0 1 0-2Z" />,
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  bookmark: <path d="M17 2H7a2 2 0 0 0-2 2v16l7-3 7 3V4a2 2 0 0 0-2-2Z" />,
  dollar: (
    <>
      <path d="M12 2v20" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </>
  ),
  chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />,
  /* instrument dial — technical confidence */
  gauge: (
    <>
      <path d="M3.5 17.5a8.5 8.5 0 1 1 17 0" />
      <path d="m12 17.5 4.6-4.6" />
      <circle cx="12" cy="17.5" r="1.5" />
    </>
  ),
  /* balance scales — professional judgment */
  scales: (
    <>
      <path d="M12 4v16M8 20h8" />
      <path d="M4.5 7.5h15" />
      <path d="m4.5 7.5-3 5.5a3 3 0 0 0 6 0Z" />
      <path d="m19.5 7.5 3 5.5a3 3 0 0 1-6 0Z" />
    </>
  ),
  /* microphone — executive communication */
  mic: (
    <>
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3M8.5 21h7" />
    </>
  ),
  /* Moss-type spherical tank on its support skirt — cargo containment */
  tank: (
    <>
      <circle cx="12" cy="9.5" r="6.5" />
      <path d="M5.8 12h12.4" />
      <path d="M7.5 15.6V21M16.5 15.6V21" />
      <path d="M4 21h16" />
    </>
  ),
  /* machinery — ship systems and propulsion */
  gear: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
    </>
  ),
  /* closed loop — the load / voyage / discharge cycle */
  cycle: (
    <>
      <path d="M21 12a9 9 0 1 1-2.6-6.4" />
      <path d="M21 3.5V9h-5.5" />
    </>
  ),
  /* linked nodes — seeing how the parts connect */
  network: (
    <>
      <circle cx="12" cy="4.5" r="2.5" />
      <circle cx="5" cy="18" r="2.5" />
      <circle cx="19" cy="18" r="2.5" />
      <path d="M10.4 6.7 6.6 15.7M13.6 6.7l3.8 9M7.5 18h9" />
    </>
  ),
  /* medal — earned credibility */
  badge: (
    <>
      <circle cx="12" cy="8.5" r="5.5" />
      <path d="m8.6 13.4-1.2 8.1 4.6-2.7 4.6 2.7-1.2-8.1" />
    </>
  ),
  /* sliders — tailored to requirements */
  sliders: (
    <>
      <path d="M4 7h10M18 7h2M4 12h4M12 12h8M4 17h8M16 17h4" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="14" cy="17" r="2" />
    </>
  ),
  /* globe — delivered anywhere */
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <ellipse cx="12" cy="12" rx="4.2" ry="9" />
    </>
  ),
  /* shield and check — safety and assurance */
  shield: (
    <>
      <path d="M12 2 4 5.5v6c0 5 3.4 8.9 8 10.5 4.6-1.6 8-5.5 8-10.5v-6L12 2Z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </>
  ),
  /* rising bars — career readiness */
  steps: (
    <>
      <rect x="3" y="14" width="5" height="7" rx="1" />
      <rect x="9.5" y="9" width="5" height="12" rx="1" />
      <rect x="16" y="4" width="5" height="17" rx="1" />
    </>
  ),
};

export default function Icon({ name, strokeWidth, className, ...rest }) {
  const path = paths[name];
  if (!path) {
    if (import.meta.env.DEV) console.warn(`[Icon] unknown icon: "${name}"`);
    return null;
  }

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className={className}
      {...STROKE}
      strokeWidth={strokeWidth ?? WIDTHS[name] ?? STROKE.strokeWidth}
      {...rest}
    >
      {path}
    </svg>
  );
}

/** Solid play triangle — filled rather than stroked, so it stands apart. */
export function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
